import { useState, useEffect } from "react";
import { API, authFetch, useEditMode } from "@/App";
import { toast } from "sonner";
import { MapPin, Plus, Trash2, Bell, AlertTriangle, CheckCircle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const GeofencingPage = () => {
  const canEdit = useEditMode();
  const [zones, setZones] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", employee_id: "", employee_name: "", center_lat: "", center_lng: "", radius_km: "5" });
  const [tab, setTab] = useState("zones");

  const fetchData = async () => {
    try {
      const [zonesRes, alertsRes, empRes] = await Promise.all([
        authFetch(`${API}/settings/geofence`),
        authFetch(`${API}/settings/geofence/alerts`),
        authFetch(`${API}/employees`),
      ]);
      const zonesData = await zonesRes.json();
      const alertsData = await alertsRes.json();
      const empData = await empRes.json();
      if (zonesData.success) setZones(zonesData.data || []);
      if (alertsData.success) setAlerts(alertsData.data || []);
      if (empData.success) setEmployees(empData.data || []);
    } catch { toast.error("Failed to load geofence data"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name || !form.employee_id || !form.center_lat || !form.center_lng) { toast.error("Fill all required fields"); return; }
    setCreating(true);
    try {
      const res = await authFetch(`${API}/settings/geofence`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          center_lat: parseFloat(form.center_lat),
          center_lng: parseFloat(form.center_lng),
          radius_km: parseFloat(form.radius_km) || 5,
        }),
      });
      if ((await res.json()).success) {
        toast.success("Geofence zone created");
        setShowAdd(false);
        setForm({ name: "", employee_id: "", employee_name: "", center_lat: "", center_lng: "", radius_km: "5" });
        fetchData();
      }
    } catch { toast.error("Failed to create zone"); }
    finally { setCreating(false); }
  };

  const handleDelete = async (id) => {
    if (!confirm("Delete this geofence zone?")) return;
    try {
      await authFetch(`${API}/settings/geofence/${id}`, { method: "DELETE" });
      toast.success("Deleted");
      fetchData();
    } catch { toast.error("Failed to delete"); }
  };

  return (
    <div className="space-y-6" data-testid="geofencing-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Sora']">Geofencing</h1>
          <p className="text-slate-500 text-sm mt-1">Set up geofence zones for field employees and monitor alerts</p>
        </div>
        {canEdit && (
          <Button onClick={() => setShowAdd(true)} className="btn-primary" data-testid="add-geofence-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Zone
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="zones" data-testid="tab-zones">Zones ({zones.length})</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts">Alerts ({alerts.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="zones" className="mt-4">
          {loading ? (
            <div className="text-center py-12"><div className="w-8 h-8 spinner mx-auto" /></div>
          ) : zones.length === 0 ? (
            <Card className="p-12 text-center">
              <MapPin className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No geofence zones configured</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {zones.map((z) => (
                <Card key={z.geofence_id} className="p-4" data-testid={`zone-${z.geofence_id}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
                        <MapPin className="w-4 h-4 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900">{z.name}</h3>
                        <p className="text-xs text-slate-500 mt-0.5">{z.employee_name}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline" className="text-[10px]">Radius: {z.radius_km} km</Badge>
                          <Badge className={`text-[10px] ${z.is_active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                            {z.is_active ? "Active" : "Inactive"}
                          </Badge>
                        </div>
                        <p className="text-[10px] text-slate-400 mt-1">
                          Lat: {z.center_lat?.toFixed(4)}, Lng: {z.center_lng?.toFixed(4)}
                        </p>
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600 hover:bg-red-50" onClick={() => handleDelete(z.geofence_id)} data-testid={`delete-zone-${z.geofence_id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="alerts" className="mt-4">
          {alerts.length === 0 ? (
            <Card className="p-12 text-center">
              <Bell className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No geofence alerts yet</p>
            </Card>
          ) : (
            <div className="space-y-2">
              {alerts.map((a, i) => (
                <Card key={a.alert_id || i} className="p-3 flex items-start gap-3" data-testid={`alert-${i}`}>
                  <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-slate-900">{a.employee_name || "Employee"} left zone "{a.zone_name || "Unknown"}"</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {a.created_at ? new Date(a.created_at).toLocaleString("en-IN") : ""} &middot; Distance: {a.distance_km?.toFixed(1) || "?"} km from center
                    </p>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Zone Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent data-testid="add-geofence-dialog">
          <DialogHeader><DialogTitle>Add Geofence Zone</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-3">
            <div>
              <label className="text-sm font-medium text-slate-700">Zone Name</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1" placeholder="e.g. Ahmedabad Office" data-testid="geofence-name" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Employee</label>
              <Select value={form.employee_id} onValueChange={(v) => {
                const emp = employees.find(e => e.employee_id === v);
                setForm({ ...form, employee_id: v, employee_name: emp?.name || "" });
              }}>
                <SelectTrigger className="mt-1" data-testid="geofence-employee"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>{employees.map(e => <SelectItem key={e.employee_id} value={e.employee_id}>{e.name} ({e.designation})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-slate-700">Latitude</label>
                <Input value={form.center_lat} onChange={(e) => setForm({ ...form, center_lat: e.target.value })} required className="mt-1" placeholder="23.0225" data-testid="geofence-lat" />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Longitude</label>
                <Input value={form.center_lng} onChange={(e) => setForm({ ...form, center_lng: e.target.value })} required className="mt-1" placeholder="72.5714" data-testid="geofence-lng" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Radius (km)</label>
              <Input type="number" step="0.5" min="0.5" value={form.radius_km} onChange={(e) => setForm({ ...form, radius_km: e.target.value })} className="mt-1" data-testid="geofence-radius" />
            </div>
            <Button type="submit" disabled={creating} className="w-full btn-primary" data-testid="submit-geofence">
              {creating ? "Creating..." : "Create Geofence Zone"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GeofencingPage;
